import { all, call, fork, put, select, takeLatest, takeLeading } from "typed-redux-saga";
import { errorActions as errorActions } from "src/components/error-viewer/error-store";
import { Action, applicationActions } from "./application-store";
import {
  ConfiguratorService,
  ContactPersonService,
  CurrentIdentitySettingService,
  CustomerService,
  Model_CurrentIdentitySettingByIdentityIdPut,
  Shared_PictureResolutions,
  SystemSettingService
} from "src/backend/openapi/v1";
import { hasValue } from "@itk/typescript-utils";
import { RootState } from "src/store";
import { ContactPersonDetailsGet, CustomerGet } from "./application-types";
import { CACHE_KEYS, CONSTANTS } from "src/utils/constants";
import { deleteCache, getCache, saveCache } from "src/utils/cache-utils";

export function* getPossibleCustomersRequest() {
  try {
    const overrideId = yield* select((state: RootState) => state.generalReducer.authorizationGateReducer.loginOverrideId);
    const possibleCustomers = yield* call(ConfiguratorService.getApiV1ConfiguratorCustomerList, undefined, overrideId);
    yield* put(applicationActions.getPossibleCustomersSuccess(possibleCustomers));
  } catch (error) {
    yield put(errorActions.addError(error));
    yield put(applicationActions.getPossibleCustomersFail());
  }
}

export function* watchGetPossibleCustomersRequest() {
  yield takeLatest(Action.GetPossibleCustomersRequest, getPossibleCustomersRequest);
}

export function* getPossibleCustomersSuccess(action: ReturnType<typeof applicationActions.getPossibleCustomersSuccess>) {
  try {
    if (action.payload.possibleCustomers.length > 0) {
      const customerId = localStorage.getItem(CONSTANTS.LOCALSTORAGE_KEYS.CUSTOMER_ID);
      let customer = action.payload.possibleCustomers.find(pc => pc.customerId === customerId);
      if (!hasValue(customer)) customer = action.payload.possibleCustomers.find(pc => pc.isDefault) || action.payload.possibleCustomers[0];

      yield put(applicationActions.getCustomerSettingsSuccess(customer));
    } else {
      throw Error("No customer found");
    }
  } catch (error) {
    yield put(errorActions.addError(error));
  }
}

export function* watchGetPossibleCustomersSuccess() {
  yield takeLatest(Action.GetPossibleCustomersSuccess, getPossibleCustomersSuccess);
}

export function* contactPersonDetails(action: ReturnType<typeof applicationActions.getContactPersonDetailsRequest>) {
  try {
    const result = yield* call(ContactPersonService.getApiV1ContactPersonDetails, undefined, action.payload.customerId);

    if (result.identityId) {
      const identityResult = yield* call(CurrentIdentitySettingService.getApiV1CurrentIdentitySettingsByIdentityId, result.identityId);
      yield put(applicationActions.getIdentitySettingsSuccess(identityResult));
    }

    yield put(applicationActions.getContactPersonDetailsSuccess(result));

    // Delte cache of profile picture to get fresh picture
    const cacheKey = CACHE_KEYS.CONTACT_PERSON_PICTURE(result.contactPersonId, Shared_PictureResolutions.Large);
    yield* call(() => deleteCache(cacheKey));
  } catch (error) {
    yield put(errorActions.addError(error));
    yield put(applicationActions.getContactPersonDetailsFail());
  }
}

export function* watchContactPersonDetails() {
  yield takeLatest(Action.GetContactPersonDetailsRequest, contactPersonDetails);
}

export function* updateContactPersonDetails(action: ReturnType<typeof applicationActions.updateContactPersonDetailsRequest>) {
  try {
    const contactPersonDetails = yield* select((state: RootState) => state.generalReducer.applicationReducer.contactPersonDetails);
    if (contactPersonDetails.loading === "has-loaded") {
      yield* call(ContactPersonService.putApiV1ContactPerson, contactPersonDetails.data.contactPersonId, {
        contactPersonId: contactPersonDetails.data.contactPersonId,
        ...action.payload.data
      });
      yield put(applicationActions.getContactPersonDetailsSuccess({ ...contactPersonDetails.data, ...(action.payload.data as ContactPersonDetailsGet) }));
    }
  } catch (error) {
    yield put(errorActions.addError(error));
  }
}

export function* watchupdateContactPersonDetails() {
  yield takeLeading(Action.UpdateContactPersonDetailRequest, updateContactPersonDetails);
}

export function* getCustomerDetails(action: ReturnType<typeof applicationActions.getCustomerRequestById>) {
  try {
    const result: CustomerGet = yield call(CustomerService.getApiV1Customer, action.payload.customerId);
    yield put(applicationActions.getCustomerSuccessById(result));
  } catch (error) {
    yield put(errorActions.addError(error));
  }
}

export function* watchGetCustomerDetails() {
  yield takeLeading(Action.GetCustomerRequestById, getCustomerDetails);
}

export function* getSystemSettingsValueById(action: ReturnType<typeof applicationActions.getSystemSettingsValueByIdRequest>) {
  try {
    let response = "";
    const data = yield* call(() => getCache(`SYSTEM-SETTINGS-${action.payload.systemSettingsId}`));

    if (!data) {
      const result: string = yield call(SystemSettingService.getApiV1SystemSettingsValue, action.payload.systemSettingsId);
      response = result;
      yield* call(() => saveCache(`SYSTEM-SETTINGS-${action.payload.systemSettingsId}`, result));
    } else {
      response = yield* call(() => data.json() as unknown as string);
    }

    yield put(applicationActions.getSystemSettingsValueByIdSuccess(action.payload.systemSettingsId, response));
  } catch (error) {
    yield put(errorActions.addError(error));
  }
}

export function* watchgetSystemSettingsValueById() {
  yield takeLeading(Action.GetSystemSettingsValueByIdRequest, getSystemSettingsValueById);
}

export function* setIdentitySettings(action: ReturnType<typeof applicationActions.setIdentitySettingsRequest>) {
  try {
    let identityId = "";
    const contactPersonDetails = yield* select((state: RootState) => state.generalReducer.applicationReducer.contactPersonDetails);
    if (contactPersonDetails.loading === "has-loaded") {
      identityId = contactPersonDetails.data.identityId || "";
    }

    const payload: Model_CurrentIdentitySettingByIdentityIdPut = {
      identityId,
      key: action.payload.key,
      value: action.payload.value
    };
    yield call(CurrentIdentitySettingService.putApiV1CurrentIdentitySettingsByIdentityId, payload);
    yield put(applicationActions.getIdentitySettingsSuccess({ [action.payload.key]: action.payload.value }));
  } catch (error) {
    yield put(errorActions.addError(error));
  }
}

export function* watchSetIdentitySettings() {
  yield takeLatest(Action.SetIdentitySettingsRequest, setIdentitySettings);
}

export function* applicationSaga() {
  yield all([
    fork(watchContactPersonDetails),
    fork(watchGetPossibleCustomersRequest),
    fork(watchGetPossibleCustomersSuccess),
    fork(watchGetCustomerDetails),
    fork(watchupdateContactPersonDetails),
    fork(watchgetSystemSettingsValueById),
    fork(watchSetIdentitySettings)
  ]);
}
